import { useState, useEffect, useMemo, useCallback } from "react";
import type { ChangeEvent } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import PageLoader from "components/common/PageLoader";
import TalentCard from "components/Screens/Marketplace/Customer/TalentNetwork/TalentCard";
import FilterDropdown from "components/Screens/Marketplace/Customer/TalentNetwork/FilterDropdown";
import InterviewSuccessModal from "components/Screens/Marketplace/Customer/TalentNetwork/InterviewSuccessModal";

import { getUserAdditionalInfo, getLoadingAdditionalInfo } from "selectors/user";

import { fetchTalentList, fetchSkills, fetchTools, requestInterview } from "services/network/talent";
import { analytics } from "services/analytics";

import SearchIcon from "@mui/icons-material/Search";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RefreshIcon from "@mui/icons-material/Refresh";

import type {
  TalentListItem,
  TalentListPagination,
  SkillOption,
  ToolOption,
} from "components/Screens/Marketplace/Customer/types";

import getTalentFullName from "components/Screens/Marketplace/Customer/utils/talentName";

import { TALENT_PROFILE_ROUTE } from "constants/routes";
import NavigationRoutes from "constants/NavigationRoutes";
import LANGUAGES from "constants/language";

import "./TalentNetworkPage.scss";

export const COUNTRIES = [
  { value: "Pakistan", label: "Pakistan" },
  // { value: "CR", label: "Costa Rica" },
  { value: "Peru", label: "Peru" },
];

const TOP_LANGUAGES = [
  { value: "English", label: "English" },
  { value: "Spanish", label: "Spanish" },
];

const ALL_LANGUAGES = LANGUAGES.slice(2).map((lang) => ({ value: lang, label: lang }));

interface CategoryGroup {
  category: string;
  items: { value: string; label: string }[];
}

function buildCategoryGroups(items: (SkillOption | ToolOption)[]): CategoryGroup[] {
  const grouped: Record<string, { value: string; label: string }[]> = {};
  items
    .filter((item) => item.active !== false)
    .forEach((item) => {
      const cat = item.category || "Other";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push({ value: item.name, label: item.name });
    });

  return Object.entries(grouped)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([category, categoryItems]) => ({
      category,
      items: categoryItems.sort((a, b) => a.label.localeCompare(b.label)),
    }));
}

function TalentNetworkPage() {
  const navigate = useNavigate();
  const [talents, setTalents] = useState<TalentListItem[]>([]);
  const [pagination, setPagination] = useState<TalentListPagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Multi-select filters (arrays)
  const [skillFilters, setSkillFilters] = useState<string[]>([]);
  const [toolFilters, setToolFilters] = useState<string[]>([]);
  const [languageFilters, setLanguageFilters] = useState<string[]>([]);
  // Single-select filter
  const [countryFilter, setCountryFilter] = useState<string[]>([]);

  // Raw API data for building category groups
  const [rawSkills, setRawSkills] = useState<SkillOption[]>([]);
  const [rawTools, setRawTools] = useState<ToolOption[]>([]);

  const [hasError, setHasError] = useState(false);

  // Interview request state
  const [requestingTalentId, setRequestingTalentId] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const { talentNetworkVisibility } = useSelector(getUserAdditionalInfo);
  const loadingUserInfo = useSelector(getLoadingAdditionalInfo);

  // Get logged-in user info from localStorage
  const loggedInUser = useMemo(() => {
    try {
      const stored = localStorage.getItem("loggedInUser");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  // Build category groups from raw data
  const skillCategoryGroups = useMemo(() => buildCategoryGroups(rawSkills), [rawSkills]);
  const toolCategoryGroups = useMemo(() => buildCategoryGroups(rawTools), [rawTools]);

  // Load filter options on mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [skills, tools] = await Promise.all([fetchSkills(), fetchTools()]);
        setRawSkills(skills);
        setRawTools(tools);
      } catch {
        setRawSkills([]);
        setRawTools([]);
      }
    };
    loadFilterOptions();
  }, []);

  // Load talents when filters or pagination change
  useEffect(() => {
    loadTalents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillFilters, toolFilters, languageFilters, countryFilter, currentPage, itemsPerPage]);

  const loadTalents = async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      const response = await fetchTalentList({
        page: currentPage,
        limit: itemsPerPage,
        country: countryFilter.length > 0 ? countryFilter[0] : undefined,
        language: languageFilters.length > 0 ? languageFilters : undefined,
        skill: skillFilters.length > 0 ? skillFilters : undefined,
        tool: toolFilters.length > 0 ? toolFilters : undefined,
        customerId: loggedInUser?.customerId || undefined,
      });
      setTalents(response.talentRecords);
      setPagination(response.pagination);
    } catch {
      setHasError(true);
      setTalents([]);
      setPagination(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTalentClick = (talentId: string) => {
    const href = TALENT_PROFILE_ROUTE.replace(":talentId", talentId);
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const handlePageChange = (newPage: number) => {
    if (pagination && newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleItemsPerPageChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleApplySkills = (values: string[]) => {
    setSkillFilters(values);
    setCurrentPage(1);
  };

  const handleApplyTools = (values: string[]) => {
    setToolFilters(values);
    setCurrentPage(1);
  };

  const handleApplyLanguages = (values: string[]) => {
    setLanguageFilters(values);
    setCurrentPage(1);
  };

  const handleApplyCountry = (values: string[]) => {
    setCountryFilter(values);
    setCurrentPage(1);
  };

  const handleSearchClick = () => {
    setCurrentPage(1);
    loadTalents();
  };

  const handleRequestInterview = useCallback(
    async (talentId: string) => {
      if (!loggedInUser) return;
      // eslint-disable-next-line no-underscore-dangle
      const talent = talents.find((t) => t._id === talentId);
      if (!talent) return;

      setRequestingTalentId(talentId);
      try {
        await requestInterview({
          customerId: loggedInUser.customerId,
          customerUserName: loggedInUser.name || "",
          customerUserEmail: loggedInUser.email || "",
          customerUserId: loggedInUser.userId || "",
          talentId,
          talentName: getTalentFullName(talent),
          talentProfileUrl: `${window.location.origin}/main/talent-network/${talentId}`,
          requestedAt: new Date().toLocaleString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }),
        });
        // Mark as requested locally
        // eslint-disable-next-line no-underscore-dangle
        setTalents((prev) => prev.map((t) => (t._id === talentId ? { ...t, interviewRequested: true } : t)));
        analytics.track("Talent Interview Requested", {
          talentId,
          talentName: `${talent.firstName} ${talent.lastName}`.trim(),
        });
        setShowSuccessModal(true);
      } catch {
        // silently fail — could add toast later
      } finally {
        setRequestingTalentId(null);
      }
    },
    [loggedInUser, talents]
  );

  const getTotalRangeText = () => {
    if (!pagination || pagination.total === 0) return "0-0 of 0";
    const start = (pagination.page - 1) * pagination.limit + 1;
    const end = Math.min(pagination.page * pagination.limit, pagination.total);
    return `${start}-${end} of ${pagination.total}`;
  };

  if (!loadingUserInfo && !talentNetworkVisibility) {
    return (
      <div className="talent-network-page">
        <div className="talent-network__header">
          <div className="talent-network__title heading-h2-semibold color-text-headings-primary">
            You are not allowed to access this page
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="talent-network-page">
      <Helmet>
        <title>Edge | Talent Network</title>
      </Helmet>
      {isLoading && <PageLoader />}
      <div className="talent-network__header">
        <div className="talent-network__header-ellipse talent-network__header-ellipse--pink" />
        <div className="talent-network__header-ellipse talent-network__header-ellipse--blue" />
        <div className="talent-network__header-content">
          <div className="talent-network__header-left">
            <div className="talent-network__title heading-h2-semibold color-text-headings-primary">Talent Network</div>
            <div className="talent-network__subtitle para-body-m-regular color-text-headings-secondary">
              Discover your next top-tier hire
            </div>
          </div>
          <div className="talent-network__header-cta">
            <div className="talent-network__cta-text">
              <span className="talent-network__cta-title">Have something specific in mind?</span>
              <span className="talent-network__cta-desc">We source any role, any industry.</span>
            </div>
            <Link
              to={NavigationRoutes.requestAHire}
              className="talent-network__cta-link"
              onClick={() => analytics.track("Hire Request Started", { context: "header" })}
            >
              Request a hire
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M6 12L10 8L6 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </div>
        </div>
      </div>
      <div className="talent-network">
        <div className="filter-bar">
          <div className="filter-bar__sections">
            <FilterDropdown
              label="Skills"
              title="What should they be good at?"
              mode="checkbox"
              selectedValues={skillFilters}
              onApply={handleApplySkills}
              categoryGroups={skillCategoryGroups}
              searchable
            />
            <FilterDropdown
              label="Tools"
              title="What tools should they know?"
              mode="checkbox"
              selectedValues={toolFilters}
              onApply={handleApplyTools}
              categoryGroups={toolCategoryGroups}
              searchable
            />
            <FilterDropdown
              label="Languages"
              title="What languages should they know?"
              mode="checkbox"
              selectedValues={languageFilters}
              onApply={handleApplyLanguages}
              topOptions={TOP_LANGUAGES}
              options={ALL_LANGUAGES}
              searchable
            />
            <FilterDropdown
              label="Country"
              title="Where should they be based?"
              mode="radio"
              selectedValues={countryFilter}
              onApply={handleApplyCountry}
              options={COUNTRIES}
              searchable={false}
            />
          </div>
          <button type="button" className="filter-bar__search-btn" onClick={handleSearchClick} aria-label="Search">
            <SearchIcon sx={{ fontSize: 24 }} />
          </button>
        </div>

        {!isLoading && pagination && pagination.total > 0 && (
          <div className="talent-network__results-count para-body-m-regular color-text-heading-secondary">
            Showing <strong>{pagination.total}</strong> talent {pagination.total === 1 ? "profile" : "profiles"}
          </div>
        )}

        <div className="talent-network__cards">
          {hasError ? (
            <div className="talent-network__error">
              <WarningAmberIcon sx={{ fontSize: 32, color: "#AFAFAF" }} />
              <div className="talent-network__error-text">
                <div className="talent-network__error-title">Something went wrong</div>
                <div className="talent-network__error-subtitle">
                  We couldn&apos;t load the profiles right now. Please try reloading.
                </div>
              </div>
              <button type="button" className="talent-network__error-cta" onClick={loadTalents}>
                <RefreshIcon sx={{ fontSize: 18, color: "#EDEDED" }} />
                Reload
              </button>
            </div>
          ) : (
            <>
              {/* eslint-disable no-underscore-dangle */}
              {Array.isArray(talents) && talents.length > 0 ? (
                talents.map((talent) => (
                  <TalentCard
                    key={talent._id}
                    talent={talent}
                    onClick={() => handleTalentClick(talent._id)}
                    onRequestInterview={handleRequestInterview}
                    isRequesting={requestingTalentId === talent._id}
                  />
                ))
              ) : (
                /* eslint-enable no-underscore-dangle */
                <div className="talent-network__empty">
                  <PersonSearchOutlinedIcon sx={{ fontSize: 36, color: "#AFAFAF" }} />
                  <div className="talent-network__empty-text">
                    <div className="talent-network__empty-title">No profiles match your search</div>
                    <div className="talent-network__empty-subtitle">
                      Your requirements are specific and that&apos;s a good thing. Share them with us and our team will
                      hand-pick the right profiles for you.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="talent-network__empty-cta"
                    onClick={() => {
                      analytics.track("Hire Request Started", { context: "no_results" });
                      navigate(NavigationRoutes.requestAHire);
                    }}
                  >
                    <PersonSearchOutlinedIcon sx={{ fontSize: 18, color: "#EDEDED" }} />
                    Request a hire
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {pagination && pagination.total > 0 && (
          <div className="talent-network__pagination">
            <div className="talent-network__pagination-info">
              <span className=" para-body-m-regular color-text-body-secondary">Profiles per page:</span>
              <select
                className="talent-network__pagination-select"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
              <span className="para-body-m-regular color-text-body-secondary">{getTotalRangeText()}</span>
            </div>

            <div className="talent-network__pagination-controls">
              <button
                className="talent-network__pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination.hasPrevPage}
                type="button"
                aria-label="Previous page"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M12.5 15L7.5 10L12.5 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <button
                className="talent-network__pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.hasNextPage}
                type="button"
                aria-label="Next page"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M7.5 15L12.5 10L7.5 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
      <InterviewSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        showStayHere={false}
        onViewOtherProfiles={() => setShowSuccessModal(false)}
      />
    </div>
  );
}

export default TalentNetworkPage;
